export type SymbolSequenceFontVariant = "8px" | "10px" | "12px";
export type SimsunAsciiFontVariant = "12px" | "13px" | "14px" | "15px" | "16px" | "17px";

const ASCII_PRINTABLE_START = 32;
const ASCII_PRINTABLE_END = 126;
const DEFAULT_BITMAP_FALLBACK_CHAR = "?";

const asciiChars = Array.from(
  { length: ASCII_PRINTABLE_END - ASCII_PRINTABLE_START + 1 },
  (_, index) => String.fromCharCode(ASCII_PRINTABLE_START + index),
).join("");

const projectSpecialChars = "◇○△▽□↺③⑤➋➌▶␣→↑";

export const SYMBOL_SEQUENCE_FONT_CHARS =
  asciiChars +
  Array.from(projectSpecialChars)
    .filter((char) => !asciiChars.includes(char))
    .join("");

export const SIMSUN_ASCII_FONT_CHARS = `${asciiChars}↑`;

export interface BitmapFontRegistrationConfig {
  readonly imageKey: string;
  readonly bitmapFontKey: string;
  readonly imagePath: string;
  readonly chars: string;
  readonly cellWidth: number;
  readonly cellHeight: number;
  readonly charsPerRow: number;
  readonly fontSize: number;
}

export interface BitmapFontLayoutProfile {
  readonly bitmapFontKey: string;
  readonly fontSize: number;
  readonly asciiAdvance: number;
  readonly spaceAdvance: number;
  readonly fullAdvance: number;
  readonly lineHeight: number;
  readonly renderableChars: string;
  readonly fallbackChar: string;
}

export interface BitmapFontConfig extends BitmapFontRegistrationConfig, BitmapFontLayoutProfile {
  readonly variant: string;
}

function createBitmapFontConfig(options: {
  readonly variant: string;
  readonly imageKey: string;
  readonly bitmapFontKey: string;
  readonly imagePath: string;
  readonly chars: string;
  readonly cellWidth: number;
  readonly cellHeight: number;
  readonly charsPerRow: number;
  readonly fontSize: number;
  readonly asciiAdvance?: number;
  readonly spaceAdvance?: number;
  readonly fullAdvance?: number;
  readonly lineHeight?: number;
  readonly fallbackChar?: string;
}): BitmapFontConfig {
  const halfWidthAdvance = Math.max(1, Math.floor(options.cellWidth / 2));

  return {
    variant: options.variant,
    imageKey: options.imageKey,
    bitmapFontKey: options.bitmapFontKey,
    imagePath: options.imagePath,
    chars: options.chars,
    cellWidth: options.cellWidth,
    cellHeight: options.cellHeight,
    charsPerRow: options.charsPerRow,
    fontSize: options.fontSize,
    asciiAdvance: options.asciiAdvance ?? halfWidthAdvance,
    spaceAdvance: options.spaceAdvance ?? options.asciiAdvance ?? halfWidthAdvance,
    fullAdvance: options.fullAdvance ?? options.cellWidth,
    lineHeight: options.lineHeight ?? options.cellHeight,
    renderableChars: options.chars,
    fallbackChar: options.fallbackChar ?? DEFAULT_BITMAP_FALLBACK_CHAR,
  };
}

export const SYMBOL_SEQUENCE_FONT_CONFIGS: Readonly<Record<SymbolSequenceFontVariant, BitmapFontConfig>> = {
  "8px": createBitmapFontConfig({
    variant: "8px",
    imageKey: "symbol-sequence-font-image-8px",
    bitmapFontKey: "symbol-sequence-font-8px",
    imagePath: "fonts/fusion-pixel-ascii-specials-8px.png",
    chars: SYMBOL_SEQUENCE_FONT_CHARS,
    cellWidth: 8,
    cellHeight: 8,
    charsPerRow: 16,
    fontSize: 8,
  }),
  "10px": createBitmapFontConfig({
    variant: "10px",
    imageKey: "symbol-sequence-font-image-10px",
    bitmapFontKey: "symbol-sequence-font-10px",
    imagePath: "fonts/fusion-pixel-ascii-specials-10px.png",
    chars: SYMBOL_SEQUENCE_FONT_CHARS,
    cellWidth: 10,
    cellHeight: 10,
    charsPerRow: 16,
    fontSize: 10,
  }),
  "12px": createBitmapFontConfig({
    variant: "12px",
    imageKey: "symbol-sequence-font-image-12px",
    bitmapFontKey: "symbol-sequence-font-12px",
    imagePath: "fonts/fusion-pixel-ascii-specials-12px.png",
    chars: SYMBOL_SEQUENCE_FONT_CHARS,
    cellWidth: 12,
    cellHeight: 12,
    charsPerRow: 16,
    fontSize: 12,
  }),
};

export const ACTIVE_SYMBOL_SEQUENCE_FONT_VARIANT: SymbolSequenceFontVariant = "12px";
export const ACTIVE_SYMBOL_SEQUENCE_FONT =
  SYMBOL_SEQUENCE_FONT_CONFIGS[ACTIVE_SYMBOL_SEQUENCE_FONT_VARIANT];

export const SYMBOL_SEQUENCE_FONT_IMAGE_KEY = ACTIVE_SYMBOL_SEQUENCE_FONT.imageKey;
export const SYMBOL_SEQUENCE_FONT_KEY = ACTIVE_SYMBOL_SEQUENCE_FONT.bitmapFontKey;
export const SYMBOL_SEQUENCE_FONT_IMAGE_PATH = ACTIVE_SYMBOL_SEQUENCE_FONT.imagePath;
export const SYMBOL_SEQUENCE_FONT_CELL_WIDTH = ACTIVE_SYMBOL_SEQUENCE_FONT.cellWidth;
export const SYMBOL_SEQUENCE_FONT_CELL_HEIGHT = ACTIVE_SYMBOL_SEQUENCE_FONT.cellHeight;
export const SYMBOL_SEQUENCE_FONT_CHARS_PER_ROW = ACTIVE_SYMBOL_SEQUENCE_FONT.charsPerRow;
export const SYMBOL_SEQUENCE_FONT_SIZE = ACTIVE_SYMBOL_SEQUENCE_FONT.fontSize;

/**
 * 8px variant used by Note UI options.
 */
export const NOTE_OPTIONS_FONT_CONFIG = SYMBOL_SEQUENCE_FONT_CONFIGS["8px"];
export const NOTE_OPTIONS_FONT_IMAGE_KEY = NOTE_OPTIONS_FONT_CONFIG.imageKey;
export const NOTE_OPTIONS_FONT_KEY = NOTE_OPTIONS_FONT_CONFIG.bitmapFontKey;
export const NOTE_OPTIONS_FONT_IMAGE_PATH = NOTE_OPTIONS_FONT_CONFIG.imagePath;
export const NOTE_OPTIONS_FONT_CELL_WIDTH = NOTE_OPTIONS_FONT_CONFIG.cellWidth;
export const NOTE_OPTIONS_FONT_CELL_HEIGHT = NOTE_OPTIONS_FONT_CONFIG.cellHeight;
export const NOTE_OPTIONS_FONT_CHARS_PER_ROW = NOTE_OPTIONS_FONT_CONFIG.charsPerRow;
export const NOTE_OPTIONS_FONT_SIZE = NOTE_OPTIONS_FONT_CONFIG.fontSize;

/**
 * 12px variant used by Note UI title.
 * Reuses the same key as the active symbol sequence font.
 */
export const NOTE_TITLE_FONT_KEY = SYMBOL_SEQUENCE_FONT_KEY;
export const NOTE_TITLE_FONT_SIZE = SYMBOL_SEQUENCE_FONT_SIZE;

function createSimsunAsciiFontConfig(size: number): BitmapFontConfig {
  return createBitmapFontConfig({
    variant: `${size}px`,
    imageKey: `simsun-ascii-font-image-${size}px`,
    bitmapFontKey: `simsun-ascii-font-${size}px`,
    imagePath: `fonts/simsun-ascii-${size}px.png`,
    chars: SIMSUN_ASCII_FONT_CHARS,
    cellWidth: size,
    cellHeight: size,
    charsPerRow: 16,
    fontSize: size,
  });
}

export const SIMSUN_ASCII_FONT_CONFIGS: Readonly<Record<SimsunAsciiFontVariant, BitmapFontConfig>> = {
  "12px": createSimsunAsciiFontConfig(12),
  "13px": createSimsunAsciiFontConfig(13),
  "14px": createSimsunAsciiFontConfig(14),
  "15px": createSimsunAsciiFontConfig(15),
  "16px": createSimsunAsciiFontConfig(16),
  "17px": createSimsunAsciiFontConfig(17),
};

export const SIMSUN_ASCII_FONT_CONFIG_LIST = Object.values(SIMSUN_ASCII_FONT_CONFIGS);
export const ACTIVE_SIMSUN_ASCII_FONT_VARIANT: SimsunAsciiFontVariant = "16px";
export const ACTIVE_SIMSUN_ASCII_FONT = SIMSUN_ASCII_FONT_CONFIGS[ACTIVE_SIMSUN_ASCII_FONT_VARIANT];

export const SIMSUN_ASCII_FONT_IMAGE_KEY = ACTIVE_SIMSUN_ASCII_FONT.imageKey;
export const SIMSUN_ASCII_FONT_KEY = ACTIVE_SIMSUN_ASCII_FONT.bitmapFontKey;
export const SIMSUN_ASCII_FONT_IMAGE_PATH = ACTIVE_SIMSUN_ASCII_FONT.imagePath;
export const SIMSUN_ASCII_FONT_CHARS_PER_ROW = ACTIVE_SIMSUN_ASCII_FONT.charsPerRow;
export const SIMSUN_ASCII_FONT_CELL_WIDTH = ACTIVE_SIMSUN_ASCII_FONT.cellWidth;
export const SIMSUN_ASCII_FONT_CELL_HEIGHT = ACTIVE_SIMSUN_ASCII_FONT.cellHeight;
export const SIMSUN_ASCII_FONT_SIZE = ACTIVE_SIMSUN_ASCII_FONT.fontSize;

export const SYMBOL_SPRITE_FONT_IMAGE_KEY = "symbol-sprite-font-image";
export const SYMBOL_SPRITE_FONT_KEY = "symbol-sprite-font";
export const SYMBOL_SPRITE_FONT_IMAGE_PATH = "sprites/TheSymbols-12x12-Sheet.png";
export const SYMBOL_SPRITE_FONT_CHARS = "◇○△▽□↺③⑤";
export const SYMBOL_SPRITE_FONT_CHARS_PER_ROW = 8;
export const SYMBOL_SPRITE_FONT_CELL_WIDTH = 12;
export const SYMBOL_SPRITE_FONT_CELL_HEIGHT = 12;
